import React, { useEffect, useState } from "react";
import {
    Modal, Form, Button, Divider, Select, InputNumber,
} from "antd";
import {
    SaveOutlined, CloseOutlined, BuildOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { viewPropertyList } from "../../Redux/Actions/propertyActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import {
    viewCountryListPagination,
    viewStateListPagination,
    viewCityListPagination,
} from "../../Redux/Actions/settingsActions";
import "./popupFeaturedBusniessProperty.css";

const { Option } = Select;

const PropertyThumb = ({ src }) => {
    const [loaded, setLoaded] = useState(false);

    return (
        <div style={{ width: 40, height: 36, borderRadius: 6, flexShrink: 0, position: "relative", overflow: "hidden", border: "1px solid #e0e0e0", background: "#f5f5f5" }}>
            {!loaded && (
                <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <BuildOutlined style={{ fontSize: 18, color: "#b0b0b0" }} />
                </div>
            )}
            {src && (
                <img
                    src={src}
                    alt=""
                    onLoad={() => setLoaded(true)}
                    style={{
                        width: "100%", height: "100%",
                        objectFit: "cover",
                        opacity: loaded ? 1 : 0,
                        transition: "opacity 0.25s ease",
                        position: "absolute", inset: 0,
                    }}
                />
            )}
        </div>
    );
};

const AddAndEditFeaturedBusinessProperty = ({ visible, onClose, propertyData, onSave }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [propertySearch, setPropertySearch] = useState("");

    // Property list
    const { enumsData } = useSelector((s) => s.enumsData || {});
    const { properties } = useSelector((s) => s.properties || {});
    const propStatus = enumsData?.data?.PropertyStatus;
    const propertyList = Array.isArray(properties?.data) ? properties.data : [];

    // Location
    // const { countryList } = useSelector((s) => s.country || {});
    // const { states } = useSelector((s) => s.states || {});
    // const cityState = useSelector((s) => s.city || {});
    // const countryData = Array.isArray(countryList?.data) ? countryList.data : [];
    // const stateData = Array.isArray(states?.data) ? states.data : [];
    // const cityData = Array.isArray(cityState?.citiesAllList?.data?.data) ? cityState?.citiesAllList?.data?.data : [];

    // Fetch enums + countries on open
    useEffect(() => {
        if (!visible) return;
        dispatch(getEnums());
        dispatch(viewCountryListPagination());
    }, [visible, dispatch]);

    // Fetch properties once enums are ready
    useEffect(() => {
        if (!visible || !propStatus) return;
        dispatch(viewPropertyList({
            status: propStatus,
            page: 1,
            limit: 1000,
            is_deleted: false,
            business_type: "business",
            filter: propertySearch,
        }));
    }, [visible, propStatus, propertySearch, dispatch]);

    // Fetch states when country changes
    useEffect(() => {
        if (!selectedCountry) return;
        dispatch(viewStateListPagination({ country: selectedCountry }));
    }, [selectedCountry, dispatch]);

    // Fetch cities when state changes
    useEffect(() => {
        if (!selectedState) return;
        dispatch(viewCityListPagination({ state: selectedState, country: selectedCountry, page: 1, limit: 1000 }));
    }, [selectedState, dispatch]);

    // Populate form on edit
    useEffect(() => {
        if (visible && propertyData) {
            const country = propertyData.country?._id || propertyData.country || undefined;
            const state = propertyData.state?._id || propertyData.state || undefined;
            const city = propertyData.city?._id || propertyData.city || undefined;

            setSelectedCountry(country || null);
            setSelectedState(state || null);

            form.setFieldsValue({
                property: propertyData.property?._id || propertyData.property || undefined,
                country,
                state,
                city,
                ranking: propertyData.ranking ?? 0,
            });
        } else if (visible) {
            form.resetFields();
            setSelectedCountry(null);
            setSelectedState(null);
        }
    }, [visible, propertyData, form]);

    const handleCountryChange = (val) => {
        setSelectedCountry(val);
        setSelectedState(null);
        form.setFieldsValue({ state: undefined, city: undefined });
    };

    const handleStateChange = (val) => {
        setSelectedState(val);
        form.setFieldsValue({ city: undefined });
    };

    const handleFinish = async (values) => {
        const payload = {
            property: values.property,
            ranking: values.ranking ?? 0,
        };
        await onSave(payload);
    };

    return (
        <Modal
            title={
                <div className="hb-modal-header-content">
                    <BuildOutlined className="hb-modal-header-icon" style={{ color: "white" }} />
                    <span style={{ color: "white" }}>
                        {propertyData?._id ? "EDIT FEATURED BUSINESS PROPERTY" : "ADD FEATURED BUSINESS PROPERTY"}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
                    onClick={onClose}
                    style={{
                        marginTop: 1, marginRight: 47, padding: 6,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: "1px solid white", color: "white",
                    }}
                    className="hb-modal-close-btn"
                />
            }
            destroyOnClose
            width={700}
            className="hb-modal-override"
            styles={{
                header: {
                    background: "#070C18",
                    borderRadius: "12px 12px 0 0",
                    padding: "20px 24px",
                    borderBottom: "none",
                },
                body: { padding: "24px", background: "#fafafa" },
            }}
        >
            <div className="hb-modal-form">
                <Form form={form} layout="vertical" onFinish={handleFinish}>

                    {/* Property Details */}
                    <div className="hb-form-section">
                        <h3 className="hb-form-section-header">Property Details</h3>

                        <Form.Item
                            label="Property"
                            name="property"
                            rules={[{ required: true, message: "Please select a property" }]}
                        >
                            <Select
                                size="large"
                                placeholder="Search and select property"
                                className="hb-form-input"
                                showSearch
                                allowClear
                                filterOption={false}
                                onSearch={(val) => setPropertySearch(val)}
                                notFoundContent="No properties found"
                                optionLabelProp="label"
                            >
                                {propertyList.map((p) => {
                                    const statusColorMap = {
                                        published: { bg: "#e6f9f0", color: "#0a7c42" },
                                        unpublished: { bg: "#fff7e6", color: "#b45309" },
                                        draft: { bg: "#f0f4ff", color: "#0D1A52" },
                                    };
                                    const statusStyle = statusColorMap[p.status] || { bg: "#f5f5f5", color: "#333" };
                                    const coverPhoto = p.cover_photos?.[0]?.url;

                                    return (
                                        <Option
                                            key={p._id}
                                            value={p._id}
                                            label={p.property_id || p._id}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
                                                {/* Cover Photo */}
                                                <PropertyThumb src={coverPhoto} />

                                                {/* ID + Address */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0D1A52" }}>
                                                        {p.property_id || "—"}
                                                    </div>
                                                    <div style={{
                                                        fontSize: 11, color: "#888",
                                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                                        maxWidth: 260,
                                                    }}>
                                                        {p.street_address || "No address"}
                                                    </div>
                                                </div>

                                                {/* Status badge */}
                                                <span style={{
                                                    background: statusStyle.bg,
                                                    color: statusStyle.color,
                                                    padding: "2px 8px",
                                                    borderRadius: 10,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: "capitalize",
                                                    flexShrink: 0,
                                                }}>
                                                    {p.status || "—"}
                                                </span>
                                            </div>
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>

                        <Form.Item label="Ranking" name="ranking">
                            <InputNumber
                                size="large"
                                min={0}
                                placeholder="Enter ranking"
                                style={{ width: "100%", borderRadius: 8 }}
                            />
                        </Form.Item>
                    </div>

                    <Divider className="hb-form-divider" />

                    {/* Location — auto-derived from property on backend */}
                    {/* <div className="hb-form-section">
                        <h3 className="hb-form-section-header">Location</h3>
                        <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
                            City, state and country are automatically derived from the selected property.
                        </p>

                        <div className="hb-form-grid">

                            <Form.Item label="Country" name="country">
                                <Select
                                    size="large"
                                    placeholder="Auto-filled from property"
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    onChange={handleCountryChange}
                                    notFoundContent="No countries available"
                                >
                                    {countryData.map((c) => (
                                        <Option key={c._id} value={c._id}>
                                            {c.country_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="State" name="state">
                                <Select
                                    size="large"
                                    placeholder={!selectedCountry ? "Select country first" : "Select state"}
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    disabled={!selectedCountry}
                                    onChange={handleStateChange}
                                    notFoundContent="No states available"
                                >
                                    {stateData.map((s) => (
                                        <Option key={s._id} value={s._id}>
                                            {s.state_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="City" name="city">
                                <Select
                                    size="large"
                                    placeholder={!selectedState ? "Select state first" : "Select city"}
                                    className="hb-form-input"
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    disabled={!selectedState}
                                    notFoundContent="No cities available"
                                >
                                    {cityData.map((c) => (
                                        <Option key={c._id} value={c._id}>
                                            {c.city_name || "—"}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                        </div>
                    </div> */}

                    <Divider className="hb-form-divider" />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <div className="hb-form-actions">
                            <Button size="large" onClick={onClose} className="hb-btn hb-btn-cancel">
                                Cancel
                            </Button>
                            <Button
                                htmlType="submit"
                                size="large"
                                icon={<SaveOutlined />}
                                className="hb-btn hb-btn-primary"
                            >
                                <span style={{ color: "white" }}>
                                    {propertyData?._id ? "Update Property" : "Add Property"}
                                </span>
                            </Button>
                        </div>
                    </Form.Item>

                </Form>
            </div>
        </Modal>
    );
};

export default AddAndEditFeaturedBusinessProperty;